// =========================================================================================@@
// Last Updated Date: May 17, 2025
// Last Updated By: Steven
// Status Level: 1
// ===========================================================================================

import React, { useContext } from 'react'
import classNames from 'classnames'
import { capitalize } from 'lodash-es'
import { NotificationManagerContext, View } from 'oio-react'
import PropTypes from 'prop-types'
import { useResendRoleParticipationEmail, useUpdateRelationship } from 'src/core/graphql/hooks'
import { Avatar } from 'src/sites/kits/UI'
import { Button } from 'src/sites/kits/UI2'

const CheckInDeskListItemRow = ({
   checkedIn,
   initiativeId,
   relationshipId,
   relationshipRoleParticipantStatus,
   roleId,
   userId,
   userAvatarUrl,
   userFullName,
   userOrganization,
   userPosition
}) => {
   const { updateRelationship, mutating } = useUpdateRelationship()
   const { resendRoleParticipationEmail } = useResendRoleParticipationEmail()
   const { showNotification } = useContext(NotificationManagerContext)

   const updateCheckedIn = async (newCheckedIn) => {
      try {
         await updateRelationship({
            userId,
            entityType: 'initiative',
            entityId: initiativeId,
            elements: [
               {
                  operation: 'checkIn',
                  checkIn: newCheckedIn
               }
            ]
         })
         // /*, {
         //    optimisticResponse: {
         //       updateRelationship: {
         //          __typename: 'Relationship',
         //          id: relationshipId,
         //          isCheckedIn: newCheckedIn
         //       }
         //    }
         // }*/
      } catch (error) {
         showNotification({
            message: error.message || 'Error updating this user, please try again',
            title: 'Error!',
            type: 'error'
         })
      }
   }

   const handleCheckIn = () => {
      updateCheckedIn(true)
   }

   const handleUnCheckIn = () => {
      updateCheckedIn(false)
   }

   const resendInvitationEmail = async () => {
      try {
         await resendRoleParticipationEmail({ relationshipId, roleId })
         showNotification({
            message: 'Invitation email sent successfully',
            title: 'Success!',
            type: 'success'
         })
      } catch (error) {
         showNotification({
            message: error.message || 'Error resending invitation email, please try again',
            title: 'Error!',
            type: 'error'
         })
      }
   }

   return (
      <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-4 w-full py-3 px-1 sm:p-3 border-b border-solid border-neutral-100 hover:bg-neutral-50 last:border-b-0 hover:border-transparent hover:rounded-lg">
         <View display="flex" alignItems="center" flex="0 0 auto" width="100px">
            <div
               className={classNames(
                  'text-xs',
                  checkedIn ? 'text-emerald-500' : 'text-neutral-400'
               )}>
               {checkedIn && '✓ Checked-in'}
               {!checkedIn &&
                  (relationshipRoleParticipantStatus === 'active'
                     ? 'Registered'
                     : capitalize(relationshipRoleParticipantStatus))}
            </div>
         </View>
         <div className="flex items-center gap-2 lg:gap-8 flex-1 text-xs">
            <Avatar
               fullName={userFullName}
               size="32px"
               src={userAvatarUrl}
            />
            <div>
               <div className="text-sm font-semibold">{userFullName}</div>
               {(userPosition || userOrganization) && (
                  <div className="text-xs text-neutral-400 line-clamp-1">
                     {userPosition}
                     {userPosition && userOrganization && ', '}
                     {userOrganization}
                  </div>
               )}
            </div>
         </div>
         <div className="w-full lg:w-auto justify-end flex flex-row-reverse lg:flex-row gap-1">
            {relationshipRoleParticipantStatus === 'invited' && (
               <Button
                  width="130px"
                  onClick={resendInvitationEmail}
                  size="xs"
                  className="w-[150px] bg-neutral-100 hover:bg-neutral-200 text-neutral-500 hover:text-neutral-700">
                  Resend Invitation Email
               </Button>
            )}
            {checkedIn && (
               <Button
                  width="130px"
                  mode={mutating ? 'loading' : 'normal'}
                  onClick={handleUnCheckIn}
                  size="xs"
                  className="w-[150px] bg-neutral-100 hover:bg-neutral-200 text-neutral-500 hover:text-neutral-700"
                  textColor="#222">
                  Set as not checked-in
               </Button>
            )}
            {!checkedIn && (
               <Button
                  width="130px"
                  mode={mutating ? 'loading' : 'normal'}
                  onClick={handleCheckIn}
                  color="#3eb471"
                  className="w-[150px] bg-emerald-100 text-emerald-600 hover:bg-emerald-600 hover:text-white"
                  size="xs">
                  ✓ Confirm Attendance
               </Button>
            )}
         </div>
      </div>
   )
}

CheckInDeskListItemRow.propTypes = {
   checkedIn: PropTypes.bool,
   initiativeId: PropTypes.string.isRequired,
   relationshipId: PropTypes.string.isRequired,
   relationshipRoleParticipantStatus: PropTypes.string.isRequired,
   roleId: PropTypes.string.isRequired,
   userId: PropTypes.string.isRequired,
   userAvatarUrl: PropTypes.string,
   userFullName: PropTypes.string.isRequired,
   userOrganization: PropTypes.string,
   userPosition: PropTypes.string
}

CheckInDeskListItemRow.defaultProps = {
   checkedIn: false,
   userAvatarUrl: undefined,
   userOrganization: undefined,
   userPosition: undefined
}

export default CheckInDeskListItemRow
